import { useEffect, useState } from "react";
import axios from "axios";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import { Eye, Users, Globe2, FileText, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#0F172A", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];

const AdminAnalytics = () => {
  const { token } = useAuth();
  const { currentProject } = useProject();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [days, setDays] = useState(30);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const scope = currentProject?.id;

  const load = async () => {
    if (!scope) return;
    setLoading(true);
    try {
      const [sRes, eRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { ...auth, params: { project_id: scope, days } }),
        axios.get(`${API}/analytics/events`, { ...auth, params: { project_id: scope, limit: 25 } }),
      ]);
      setSummary(sRes.data);
      setEvents(eRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, days]);

  if (!currentProject) {
    return (
      <AdminLayout title="Analytics">
        <p className="text-slate-600">Select a project in the top dropdown to view analytics.</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`Analytics — ${currentProject.name}`}>
      <div className="space-y-6" data-testid="admin-analytics-page">
        {/* Controls */}
        <Card>
          <CardContent className="p-4 flex flex-col md:flex-row gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Window:</label>
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="w-32" data-testid="analytics-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24h</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500 ml-auto">
              Looking at <span className="font-medium text-slate-700">{currentProject.name}</span>
              {currentProject.domain ? ` · ${currentProject.domain}` : ""}
            </p>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-analytics">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </CardContent>
        </Card>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi icon={<Eye className="h-5 w-5" />} label="Page views" value={summary?.total_views ?? 0} />
          <Kpi icon={<Users className="h-5 w-5" />} label="Unique visitors" value={summary?.unique_visitors ?? 0} />
          <Kpi icon={<Globe2 className="h-5 w-5" />} label="Countries" value={summary?.top_countries?.length ?? 0} />
          <Kpi icon={<FileText className="h-5 w-5" />} label="Pages tracked" value={summary?.top_pages?.length ?? 0} />
        </div>

        {/* Per-project breakdown is shown in Global Analytics page — omitted here */}

        {/* Timeline */}
        <Card>
          <CardHeader><CardTitle>Traffic timeline</CardTitle></CardHeader>
          <CardContent style={{ height: 280 }}>
            {summary?.timeline?.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary.timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-500 flex items-center justify-center h-full">No traffic in selected window yet.</p>
            )}
          </CardContent>
        </Card>

        {/* Top pages + Top referrers */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RankList title="Top pages" rows={summary?.top_pages || []} />
          <RankList title="Top referrers" rows={(summary?.top_referrers || []).map((r) => ({ ...r, label: r.label || "(direct)" }))} />
        </div>

        {/* Countries pie + browsers bar + devices */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <DonutCard title="Top countries" rows={summary?.top_countries || []} />
          <BarCard title="Browsers" rows={summary?.browsers || []} />
          <BarCard title="Devices" rows={summary?.devices || []} />
        </div>

        {/* Live events */}
        <Card>
          <CardHeader><CardTitle>Recent activity</CardTitle></CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <p className="text-slate-500">No recent events.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="events-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
                      <th className="py-2 pr-3">Time</th>
                      <th className="py-2 pr-3">Path</th>
                      <th className="py-2 pr-3">Country</th>
                      <th className="py-2 pr-3">Browser</th>
                      <th className="py-2 pr-3">Device</th>
                      <th className="py-2 pr-3">Referrer</th>
                      <th className="py-2 pr-3">IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-b last:border-b-0" data-testid={`event-row-${e.id}`}>
                        <td className="py-2 pr-3 whitespace-nowrap text-slate-500">{new Date(e.timestamp).toLocaleString()}</td>
                        <td className="py-2 pr-3 max-w-xs truncate font-medium">{e.path}</td>
                        <td className="py-2 pr-3">{e.country || "—"}{e.city ? <span className="text-slate-400"> · {e.city}</span> : null}</td>
                        <td className="py-2 pr-3">{e.browser || "—"}</td>
                        <td className="py-2 pr-3">{e.device || "—"}</td>
                        <td className="py-2 pr-3 max-w-xs truncate text-slate-500">{e.referrer || "(direct)"}</td>
                        <td className="py-2 pr-3 font-mono text-xs text-slate-400">{e.ip || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

const Kpi = ({ icon, label, value }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center gap-3 text-slate-500 text-sm mb-2">{icon}<span>{label}</span></div>
      <p className="text-3xl font-bold text-slate-900">{value.toLocaleString()}</p>
    </CardContent>
  </Card>
);

const RankList = ({ title, rows }) => (
  <Card>
    <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
    <CardContent>
      {rows.length === 0 ? (
        <p className="text-slate-500 text-sm">No data.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r, i) => {
            const max = rows[0]?.count || 1;
            const pct = (r.count / max) * 100;
            return (
              <li key={i} className="flex items-center gap-3">
                <span className="w-6 text-xs text-slate-400">#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="truncate text-sm">{r.label || "(blank)"}</span>
                    <span className="text-sm font-semibold ml-2">{r.count}</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </CardContent>
  </Card>
);

const DonutCard = ({ title, rows }) => {
  const data = (rows || []).slice(0, 8).map((r) => ({ name: r.label || "Unknown", value: r.count }));
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent style={{ height: 240 }}>
        {data.length === 0 ? (
          <p className="text-slate-500 text-sm">No data.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {data.map((d, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

const BarCard = ({ title, rows }) => {
  const data = (rows || []).slice(0, 8).map((r) => ({ name: r.label || "Other", value: r.count }));
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent style={{ height: 240 }}>
        {data.length === 0 ? (
          <p className="text-slate-500 text-sm">No data.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip />
              <Bar dataKey="value" fill="#3B82F6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAnalytics;
